// TaxCalendarDrawerContent — Drilldown fuer TaxCalendar-Tile.
//
// Spec: "alle 90-Tage-Items chronologisch".
// Volle Liste mit Severity-Highlight (pink <14T, amber <30T, cyan sonst)
// + Firmen-Dot + Betrag.

import type { ComplianceItem, ComplianceResponse } from "../../../lib/api";
import { formatEUR } from "../../../lib/api";
import { firmaDot } from "../../../lib/firma-colors";

interface Props {
  data?: ComplianceResponse | null;
  loading?: boolean;
}

const ART_KURZ: Record<string, string> = {
  ust_va_eintrag: "USt-VA",
  steuer_zahlung: "Steuerzahlung",
  bescheinigung_ablauf: "Bescheinigung",
};

function severityClass(it: ComplianceItem): string {
  if (it.days_until <= 14) return "border-pink-500/40 bg-pink-500/[0.05]";
  if (it.days_until <= 30) return "border-amber-500/40 bg-amber-500/[0.04]";
  return "border-cyan-500/30 bg-cyan-500/[0.03]";
}

export default function TaxCalendarDrawerContent({ data, loading }: Props) {
  if (loading) {
    return (
      <div className="text-xs text-slate-400">Lade Steuer-Termine …</div>
    );
  }
  const items = data?.items ?? [];

  return (
    <>
      <section className="flex items-baseline justify-between text-xs text-slate-300 border border-slate-700/60 rounded-lg px-3 py-2">
        <span>{data?.n_total ?? items.length} Termine 90 Tage</span>
        <span className="text-pink-300">
          {data?.n_critical ?? 0} kritisch (≤14T)
        </span>
      </section>

      {items.length === 0 ? (
        <p className="text-xs text-slate-500">
          Keine Termine 90T. Quelle: steuer_termin (Pahl-Diederichsen) +
          bescheinigung-Ablauf.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it) => (
            <li
              key={`${it.datum}-${it.firma_id}-${it.art}`}
              className={`rounded-md border px-3 py-2 flex items-baseline gap-3 ${severityClass(it)}`}
            >
              <span
                aria-hidden
                className={`h-1.5 w-1.5 rounded-full shrink-0 ${firmaDot(it.firma_id)}`}
              />
              <span className="text-[10px] tabular-nums text-slate-300 shrink-0">
                {it.datum.slice(8, 10)}.{it.datum.slice(5, 7)}.{it.datum.slice(2, 4)}
              </span>
              <span className="text-xs text-slate-100 truncate flex-1 min-w-0">
                <span className="text-[10px] text-slate-400 mr-1">
                  [{ART_KURZ[it.art] ?? it.typ}]
                </span>
                {it.titel}
              </span>
              <span className="text-[10px] tabular-nums text-slate-300 shrink-0 text-right">
                {it.betrag_eur ? formatEUR(it.betrag_eur) : "—"}
                <br />
                <span className={it.days_until <= 14 ? "text-pink-300" : "text-slate-500"}>
                  {it.days_until}d
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
